import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { BrowserRouter, useNavigate } from 'react-router-dom';

import { ErrorBoundary } from './components/atom/ErrorBoundary/ErrorBoundary';
import AppDialog from './components/template/AppDialog/AppDialog';
import { config } from './config';
import { main } from './configuration';
import { DEFAULT_LOCALE, initIntl } from './configuration/lang/lang';
import { getDisplayMessages, getLocale } from './configuration/lang/langSlice';
import { handleLoginRedirect } from './configuration/login/redirect';
import { useAppSelector } from './configuration/setup/hooks';
import App from './layout/App';
import { setNavigate } from './routes/navigationHelper';
import { reduxStore } from './store/store';

// INFO: Used as a provider to expose the navigate function to outside React's lifecycle
const NavigationProvider = () => {
  const navigate = useNavigate();

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  return null;
};

const Root = () => {
  const userLocale = useAppSelector(getLocale);
  const displayMessages = useAppSelector(getDisplayMessages);
  
  initIntl(userLocale, displayMessages);
  
  return (
    <IntlProvider
      defaultLocale={DEFAULT_LOCALE}
      key={userLocale}
      locale={userLocale ?? 'de'}
      messages={displayMessages ?? {}}
    >
      <NavigationProvider />
      <AppDialog />
      <App />
    </IntlProvider>
  );
};

const renderApplication = () => {
  createRoot(document.getElementById('root') as HTMLElement).render(
    <StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <Provider store={reduxStore}>
            <Root />
          </Provider>
        </BrowserRouter>
      </ErrorBoundary>
    </StrictMode>,
  );
};

const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;
const isProdPreview = import.meta.env.VITE_PRODUCTION_PREVIEW;
const nonMSWTestingURL = import.meta.env.VITE_NON_MSW_API_BASE_URL_FOR_TESTING;

if ((isDev && config.enableMockServer) || isProdPreview) {
  import('../mocks/browser')
    .then(({ startWorker }) => startWorker(nonMSWTestingURL))
    .then(() => main(renderApplication));
}

if (window.location.href.startsWith(config.login.redirectUri as string)) {
  handleLoginRedirect();
} else if (isProd && !isProdPreview) {
  main(renderApplication);
}

